import React from 'react';
import { useFigureStore } from './store';
import ManualContour, { calculateControlPoints } from './ManualContour';
import BoxCanvas from './BoxCanvas';
import NewFigureDialog from './NewFigureDialog';
import { FigureToolbar, FigureSidebar } from './BoxResizerParts';

// `onCreateFigure(type, coords)` (coords may be null → caller derives them)
// resolves to the new figure id; `onRemoveFigure(id)` deletes it. Transport is
// owned by the caller (GraphQL in the SPA).
export default function BoxResizer({
  grave,
  sites,
  image,
  page,
  setFigures,
  figures,
  removeFigure,
  addFigure,
  onCreateFigure,
  onRemoveFigure,
  onFinish
}) {
  const [creatingNewFigure, setCreatingNewFigure] = React.useState(false);
  const [currentEditBox, setCurrentEditBox] = React.useState(
    grave.figures.filter((f) => f.type === 'Lithic')[0]?.id
  );
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [newFigureType, setNewFigureType] = React.useState(null);
  const [newFigureCoords, setNewFigureCoords] = React.useState(null);
  const divRef = React.useRef(null);

  React.useEffect(() => {
    setFigures(
      grave.figures.map((figure) => {
        if (figure.control_point_1_x === null) {
          const cps = calculateControlPoints(figure);
          return {
            typeName: figure.type,
            ...figure,
            control_point_1_x: cps[0].x,
            control_point_1_y: cps[0].y,
            control_point_2_x: cps[1].x,
            control_point_2_y: cps[1].y,
            control_point_3_x: cps[2].x,
            control_point_3_y: cps[2].y,
            control_point_4_x: cps[3].x,
            control_point_4_y: cps[3].y,
            anchor_point_1_x: figure.x1,
            anchor_point_1_y: figure.y1,
            anchor_point_2_x: figure.x2,
            anchor_point_2_y: figure.y1,
            anchor_point_3_x: figure.x2,
            anchor_point_3_y: figure.y2,
            anchor_point_4_x: figure.x1,
            anchor_point_4_y: figure.y2
          };
        }
        return { typeName: figure.type, ...figure };
      })
    );
  }, []);

  React.useEffect(() => {
    if (divRef.current) {
      divRef.current.style.cursor = isDrawing ? 'crosshair' : 'default';
    }
  }, [isDrawing]);

  function startDrawing(type) {
    setIsDrawing(true);
    setNewFigureType(type);
    setNewFigureCoords(null);
    setCurrentEditBox(null);
  }

  async function finishDrawing() {
    if (!isDrawing || !newFigureCoords || !newFigureType) {
      setIsDrawing(false);
      setNewFigureType(null);
      setNewFigureCoords(null);
      return;
    }
    if (newFigureCoords.x1 === newFigureCoords.x2 || newFigureCoords.y1 === newFigureCoords.y2) {
      setIsDrawing(false);
      setNewFigureType(null);
      setNewFigureCoords(null);
      return;
    }

    const normalized = {
      x1: Math.min(newFigureCoords.x1, newFigureCoords.x2),
      y1: Math.min(newFigureCoords.y1, newFigureCoords.y2),
      x2: Math.max(newFigureCoords.x1, newFigureCoords.x2),
      y2: Math.max(newFigureCoords.y1, newFigureCoords.y2)
    };
    setIsDrawing(false);
    setNewFigureType(null);
    setNewFigureCoords(null);
    const id = await onCreateFigure(newFigureType, normalized);
    setCurrentEditBox(id);
  }

  async function removeEditBox(id) {
    await onRemoveFigure(id);
  }

  // Konva handlers (Box, AnchorPoint, ControlPoint) report changes as
  // onChangeFigure(id, figure); the sidebar reports onChangeFigure(figure).
  // Both forms must update the store, otherwise the box snaps back on re-render.
  function onChangeFigure(id, figure) {
    const updated = id !== null && typeof id === 'object' ? id : figure;
    if (!updated || updated.id === undefined) return;
    setFigures(
      Object.values(figures).map((current) => (current.id === updated.id ? updated : current))
    );
  }

  function setManualBoundingBox(figure, checked) {
    if (checked && figure.bounding_box_angle === null) {
      onChangeFigure({
        ...figure,
        manual_bounding_box: checked,
        bounding_box_center_x: (figure.x1 + figure.x2) / 2,
        bounding_box_center_y: (figure.y1 + figure.y2) / 2,
        bounding_box_angle: 0,
        bounding_box_width: figure.x2 - figure.x1,
        bounding_box_height: figure.y2 - figure.y1
      });
    } else {
      onChangeFigure({ ...figure, manual_bounding_box: checked });
    }
  }

  const isGrave = grave.type === 'Grave';
  const validations = (
    isGrave ? ['Spine', 'SkeletonFigure', 'Scale', 'GraveCrossSection', 'Arrow', 'Good'] : ['Scale']
  ).map((item) => {
    const exists = Object.values(figures).some((fig) => fig.typeName === item);
    if (!exists) {
      const warning = item === 'SkeletonFigure' || item === 'Spine';
      return (
        <li
          key={item}
          className={`box-resizer-validation-item ${warning ? 'alert-warning' : 'alert-danger'}`}
        >
          {item} is missing
        </li>
      );
    }
    return null;
  });

  return (
    <>
      {creatingNewFigure && (
        <NewFigureDialog
          addFigure={(type) => onCreateFigure(type, null).then(setCurrentEditBox)}
          closeDialog={() => setCreatingNewFigure(false)}
        />
      )}
      <FigureToolbar onTypeSelect={startDrawing} />
      <div className="box-resizer-layout">
        <div className="box-resizer-canvas-col card" ref={divRef}>
          <BoxCanvas
            setCurrentEditBox={setCurrentEditBox}
            divRef={divRef}
            image={image}
            figures={figures}
            currentEditBox={currentEditBox}
            onChangeFigure={onChangeFigure}
            isDrawing={isDrawing}
            newFigureCoords={newFigureCoords}
            setNewFigureCoords={setNewFigureCoords}
            finishDrawing={finishDrawing}
          />
        </div>
        <div className="box-resizer-sidebar-col">
          <FigureSidebar
            figures={figures}
            currentEditBox={currentEditBox}
            setCurrentEditBox={setCurrentEditBox}
            removeEditBox={removeEditBox}
            setManualBoundingBox={setManualBoundingBox}
            setCreatingNewFigure={setCreatingNewFigure}
            onFinish={onFinish}
          />
        </div>
      </div>
      <ul className="box-resizer-validation-list">{validations}</ul>
    </>
  );
}
